/**
 * multilang.ts
 * ------------
 * Parse multilingual content embedded with brace-tag syntax:
 *
 *   {en}Hello{en}{ar}مرحبا{ar}
 *
 * Rules:
 *   1. If the string contains no tags → return it unchanged.
 *   2. Look for a block matching the requested language code (case-insensitive).
 *   3. Fallback chain: requested lang → 'en' → first available block → raw string.
 *
 * The parser is pure and has no React dependency.
 *
 * Usage:
 *   import { parseMultilang, hasMultilangTags, extractLanguages } from '@/utils/multilang';
 */

// Matches {xx}...{xx} where xx is 2-5 lower/upper-case letters.
// Lazy match ([\s\S]*?) so nested blocks don't bleed into each other.
const TAG_RE = /\{([a-zA-Z]{2,5})\}([\s\S]*?)\{\1\}/g;

export interface MultilangBlock {
  lang: string;
  content: string;
}

/**
 * Parse all language blocks out of a string.
 * Returns an empty array when the string has no multilang tags.
 */
export function parseBlocks(text: string): MultilangBlock[] {
  const blocks: MultilangBlock[] = [];
  let match: RegExpExecArray | null;
  // Reset lastIndex so calling this multiple times on the same string is safe.
  TAG_RE.lastIndex = 0;
  while ((match = TAG_RE.exec(text)) !== null) {
    blocks.push({ lang: match[1].toLowerCase(), content: match[2].trim() });
  }
  return blocks;
}

/** Returns true when the string contains at least one {lang}…{lang} block. */
export function hasMultilangTags(text: string): boolean {
  TAG_RE.lastIndex = 0;
  return TAG_RE.test(text);
}

/**
 * Return the set of language codes present in a string.
 * e.g. "{en}Hi{en}{ar}مرحبا{ar}" → ['en', 'ar']
 */
export function extractLanguages(text: string): string[] {
  return parseBlocks(text).map(b => b.lang);
}

/**
 * Main filter function.
 *
 * @param text   The raw stored string, possibly containing {lang}…{lang} blocks.
 * @param lang   The active UI language code (e.g. 'en', 'ar').
 * @returns      The content for the requested language, or a fallback.
 */
export function parseMultilang(
  text: string | null | undefined,
  lang: string,
): string {
  if (!text) return text ?? '';

  // No multilang tags → pass through as-is (plain content).
  if (parseBlocks(text).length === 0) return text;

  const target = lang.toLowerCase();
  // Each stored value (a run of adjacent blocks) is resolved on its own and the text around it kept, so a full
  // name built from two stored names, or a sentence with a stored title in it, comes out whole.
  return text.replace(RUN_RE, (run) => {
    const blocks = parseBlocks(run);
    // 1. Exact language match → 2. English → 3. first available block
    return (blocks.find(b => b.lang === target) ?? blocks.find(b => b.lang === 'en') ?? blocks[0])?.content ?? run;
  }).trim();
}

// A run of adjacent blocks = one stored value.
const RUN_RE = /(?:\{([a-zA-Z]{2,5})\}[\s\S]*?\{\1\})(?:\{([a-zA-Z]{2,5})\}[\s\S]*?\{\2\})*/g;

/**
 * Replace ONE language's text inside a stored multilang string, keeping every
 * other translation. Used by quick single-field edits (e.g. the inline module /
 * activity title in the course builder) so typing in Arabic doesn't wipe the
 * English and Russian versions.
 *
 *   setMultilangSlot('{en}Intro{en}{ar}مقدمة{ar}', 'ar', 'تمهيد')
 *     → '{en}Intro{en}{ar}تمهيد{ar}'
 *
 * A plain string (no tags) is simply replaced, as before.
 */
export function setMultilangSlot(raw: string | null | undefined, lang: string, text: string): string {
  const blocks = parseBlocks(raw ?? '');
  if (blocks.length === 0) return text;
  const code = lang.toLowerCase();
  const next = blocks.some(b => b.lang === code)
    ? blocks.map(b => (b.lang === code ? { ...b, content: text } : b))
    : [...blocks, { lang: code, content: text }];
  return next
    .filter(b => b.content.trim())
    .map(b => `{${b.lang}}${b.content.trim()}{${b.lang}}`)
    .join('');
}
